import { Box, Tooltip, TooltipProps } from '@mui/material';
import { ReactNode, useRef } from 'react';
import { ElementSize, useElementSize } from '~/hooks/useElementSize';

interface FittedPaneProps {
    /** The hover hint over the whole box, saying what the pane shows. */
    readonly hint: string;
    /** Where the hint opens from the pointer; above it by default. */
    readonly hintPlacement?: TooltipProps['placement'];
    readonly testId: string;
    /**
     * Draws the pane for the measured box, or nothing while the box leaves
     * it no room. Not called before the first measurement.
     */
    readonly children: (box: ElementSize) => ReactNode;
}

/**
 * A pane fitted to the box it is given: the box is measured live, so the
 * drawing follows a resized or maximized tile, and the pane is centred in
 * it. Nothing is drawn before the first measurement. What the box's size
 * means for the drawing is the child's rule. The hint follows the pointer:
 * the box fills the pane, so every side of it borders a control of the
 * tile or of the row, while the pointer's own surroundings are canvas.
 */
export default function FittedPane({
    hint,
    hintPlacement = 'top',
    testId,
    children,
}: FittedPaneProps) {
    const boxRef = useRef<HTMLDivElement | null>(null);
    const size = useElementSize(boxRef);
    return (
        <Tooltip title={hint} placement={hintPlacement} followCursor>
            <Box
                ref={boxRef}
                data-testid={testId}
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {size ? children(size) : null}
            </Box>
        </Tooltip>
    );
}
